import React from "react"
import {ok} from "neverthrow"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import {DetailsList} from "../DetailsList/DetailsList"
import {AssignmentPageItem} from "../AssignmentPageItem/AssignmentPageItem"
import {
	Assignee,
	AssignmentActionRequestType,
	AssignmentStage,
	AssignmentStatus,
	update,
	NameMismatchAssignmentItemPayload,
	NameMismatchApproveRequest,
	NameMismatchRejectRequest,
	IncomingWireRejectReason,
	incomingWireRejectReasons,
} from "../../resources/assignments"
import {useResetScrollTop} from "../../hooks/useResetScrollTop"
import {AssignmentPageItemApproveRejectFooter} from "../AssignmentPageItemApproveRejectFooter/AssignmentPageItemApproveRejectFooter"
import {useModal} from "react-modal-hook"
import {AssignmentApproveRejectModal} from "../AssignmentApproveRejectModal/AssignmentApproveRejectModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {AssignmentPageCompletedItemHeader} from "../AssignmentPageCompletedItemHeader/AssignmentPageCompletedItemHeader"
import {getWireReceivedPaymentCreditorName, WireReceivedPayment} from "../../resources/receivedPayments"
import {Account} from "../../resources/account"
import {WireReceivedPaymentDetails} from "../WireReceivedPaymentDetails/WireReceivedPaymentDetails"
import {NamesScoreTable} from "../NamesScoreTable/NamesScoreTable"
import {CustomerKnownNames, getCustomerKnownNames, addCustomerKnownNames} from "../../resources/customerKnownNames"
import {Application, getApplication} from "../../resources/application"
import {Customer, getCustomer, getCustomerName, isBusinessCustomer} from "../../resources/customer"
import {Org, getOrg} from "../../resources/org"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResultZipComponent} from "../../containers/AsyncResult/AsyncResult"
import Skeleton from "react-loading-skeleton"
import {Badge} from "../Badge/Badge"
import classNames from "classnames"
import {NAME_MISMATCH_PARTY_ROLE_LABEL, normalizeNameMatchKey} from "./nameMismatchPartyRoles"
import {WireCounterparty} from "../../resources/payment"

interface AssignmentPageItemNameMismatchProps {
	id: string
	assignees: Assignee[]
	status: AssignmentStatus
	stage: AssignmentStage
	title: string
	description?: string | JSX.Element
	createdAt: Date
	statusUpdatedAt?: Date
	isOwnersItem?: boolean
	account?: Account
	wireReceivedPayment?: WireReceivedPayment
	payload: NameMismatchAssignmentItemPayload
	accessToken: string
	refresh: () => void
	disabled?: boolean
	counterparty?: WireCounterparty
}

type NameRoleMap = Record<string, string[]>

type NameInfo = {displayName: string; roles: string[]}
type NameInfoMap = Record<string, NameInfo>

function asFullName(firstName?: string, lastName?: string) {
	return [firstName, lastName].filter(Boolean).join(" ").trim()
}

function addNameInfo(map: NameInfoMap, name: string | undefined, role: string) {
	if (!name) {
		return
	}

	const trimmed = name.trim().replace(/\s+/g, " ")
	const key = normalizeNameMatchKey(trimmed)
	if (!key) {
		return
	}

	const existing = map[key]
	if (existing) {
		if (!existing.roles.includes(role)) {
			existing.roles.push(role)
		}
		return
	}

	map[key] = {displayName: trimmed, roles: [role]}
}

function buildNameInfoMap({
	customerResponse,
	customerKnownNamesResponse,
	applicationResponse,
	org,
}: {
	customerResponse?: OkDocument<Customer>
	customerKnownNamesResponse?: OkDocument<CustomerKnownNames>
	applicationResponse?: OkDocument<Application>
	org?: Org
}): NameInfoMap {
	const nameInfoMap: NameInfoMap = {}

	if (customerResponse) {
		addNameInfo(nameInfoMap, getCustomerName(customerResponse.data), NAME_MISMATCH_PARTY_ROLE_LABEL.customer)
	}

	customerKnownNamesResponse?.data.attributes.names?.forEach((name) => {
		addNameInfo(nameInfoMap, name, NAME_MISMATCH_PARTY_ROLE_LABEL.knownAlias)
	})

	customerResponse?.data.attributes.authorizedUsers?.forEach((authorizedUser) => {
		addNameInfo(
			nameInfoMap,
			asFullName(authorizedUser.fullName.first, authorizedUser.fullName.last),
			NAME_MISMATCH_PARTY_ROLE_LABEL.authorizedUser
		)
	})
	if (customerResponse && isBusinessCustomer(customerResponse.data)) {
		addNameInfo(
			nameInfoMap,
			asFullName(
				customerResponse.data.attributes.contact.fullName.first,
				customerResponse.data.attributes.contact.fullName.last
			),
			NAME_MISMATCH_PARTY_ROLE_LABEL.contactPerson
		)
	}

	if (applicationResponse?.data.type === "businessApplication") {
		applicationResponse.data.attributes.beneficialOwners?.forEach((beneficialOwner) => {
			addNameInfo(
				nameInfoMap,
				asFullName(beneficialOwner.fullName.first, beneficialOwner.fullName.last),
				NAME_MISMATCH_PARTY_ROLE_LABEL.beneficialOwner
			)
		})

		addNameInfo(
			nameInfoMap,
			asFullName(
				applicationResponse.data.attributes.officer?.fullName.first,
				applicationResponse.data.attributes.officer?.fullName.last
			),
			NAME_MISMATCH_PARTY_ROLE_LABEL.officer
		)
	}

	if (org) {
		addNameInfo(nameInfoMap, org.attributes.name, NAME_MISMATCH_PARTY_ROLE_LABEL.org)
	}

	return nameInfoMap
}

function toNameRoleMap(nameInfoMap: NameInfoMap): NameRoleMap {
	return Object.fromEntries(Object.entries(nameInfoMap).map(([key, {roles}]) => [key, roles]))
}

// Names known on the customer / application that the backend did not include in
// knownNameMatchScoresMap for this assignment. The UI surfaces them so reviewers
// can see the full set of "Names at Unit", not just the ones that were scored.
function pickUnscoredNames(nameInfoMap: NameInfoMap, namesScoreMap: Record<string, string>): string[] {
	const scoredKeys = new Set(Object.keys(namesScoreMap).map(normalizeNameMatchKey))
	return Object.entries(nameInfoMap)
		.filter(([key]) => !scoredKeys.has(key))
		.map(([, {displayName}]) => displayName)
}

interface NameMismatchNamesScoreTableProps {
	accessToken: string
	customerId: string
	namesScoreMap: Record<string, string>
	disabled?: boolean
}

function NameMismatchNamesScoreTablePending({
	namesScoreMap,
	disabled,
}: {
	namesScoreMap: Record<string, string>
	disabled?: boolean
}) {
	const rows = Object.entries(namesScoreMap).sort(
		([, scoreA], [, scoreB]) => parseInt(scoreB, 10) - parseInt(scoreA, 10)
	)

	return (
		<div className="names-score-table">
			<table className="has-type-column">
				<thead>
					<tr>
						<th className="table-header">Names</th>
						<th className="table-header">Type</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(([name]) => (
						<tr key={name}>
							<td className={classNames("name-cell", disabled && "disabled")}>
								<Badge className="badge-text" text={name} />
							</td>
							<td className={classNames(disabled && "disabled")}>
								<Skeleton width={96} />
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

interface NameMismatchNamesScoreTableWithApplicationProps {
	accessToken: string
	customerResponse: OkDocument<Customer>
	customerKnownNamesResponse: OkDocument<CustomerKnownNames>
	namesScoreMap: Record<string, string>
	disabled?: boolean
}

function useCustomerOrg(accessToken: string, orgId: string | undefined) {
	return useAsyncResult<Org | null, ErrorDocument>(() => {
		if (!orgId) {
			return Promise.resolve(ok(null))
		}

		return getOrg(accessToken, orgId).then((result) => result.map((org) => org as Org | null))
	}, [accessToken, orgId])
}

function NameMismatchNamesScoreTableWithApplication({
	accessToken,
	customerResponse,
	customerKnownNamesResponse,
	namesScoreMap,
	disabled,
}: NameMismatchNamesScoreTableWithApplicationProps) {
	const applicationId = customerResponse.data.relationships.application?.data.id
	const applicationResult = useAsyncResult<OkDocument<Application> | null, ErrorDocument>(() => {
		if (!applicationId) {
			return Promise.resolve(ok(null))
		}

		return getApplication(accessToken, applicationId).then((result) =>
			result.map((application) => application as OkDocument<Application> | null)
		)
	}, [accessToken, applicationId])

	const orgId = customerResponse.data.relationships.org?.data.id
	const orgResult = useCustomerOrg(accessToken, orgId)

	const nameInfoMap = React.useMemo(
		() =>
			buildNameInfoMap({
				customerResponse,
				customerKnownNamesResponse,
				applicationResponse: applicationResult.isOk() ? applicationResult.value || undefined : undefined,
				org: orgResult.isOk() ? orgResult.value || undefined : undefined,
			}),
		[customerResponse, customerKnownNamesResponse, applicationResult, orgResult]
	)

	const nameRoleMap = React.useMemo(() => toNameRoleMap(nameInfoMap), [nameInfoMap])
	const unscoredNames = React.useMemo(() => pickUnscoredNames(nameInfoMap, namesScoreMap), [nameInfoMap, namesScoreMap])

	return (
		<NamesScoreTable
			namesScoreMap={namesScoreMap}
			nameRoleMap={nameRoleMap}
			unscoredNames={unscoredNames}
			disabled={disabled}
		/>
	)
}

function NameMismatchNamesScoreTable({
	accessToken,
	customerId,
	namesScoreMap,
	disabled,
}: NameMismatchNamesScoreTableProps) {
	const customerResult = useAsyncResult<OkDocument<Customer>, ErrorDocument>(
		() => getCustomer(accessToken, customerId),
		[accessToken, customerId]
	)
	const customerKnownNamesResult = useAsyncResult<OkDocument<CustomerKnownNames>, ErrorDocument>(
		() => getCustomerKnownNames(accessToken, customerId),
		[accessToken, customerId]
	)

	return (
		<AsyncResultZipComponent
			asyncResult1={customerResult}
			asyncResult2={customerKnownNamesResult}
			pendingComponent={<NameMismatchNamesScoreTablePending namesScoreMap={namesScoreMap} disabled={disabled} />}
		>
			{({value1: customerResponse, value2: customerKnownNamesResponse}) => (
				<NameMismatchNamesScoreTableWithApplication
					accessToken={accessToken}
					customerResponse={customerResponse}
					customerKnownNamesResponse={customerKnownNamesResponse}
					namesScoreMap={namesScoreMap}
					disabled={disabled}
				/>
			)}
		</AsyncResultZipComponent>
	)
}

export function AssignmentPageItemNameMismatch({
	id,
	assignees,
	status,
	stage,
	title,
	description,
	createdAt,
	statusUpdatedAt,
	isOwnersItem,
	account,
	wireReceivedPayment,
	payload: {knownNameMatchScoresMap, receivingEntityName, sendingEntityName, reason, reasonText},
	accessToken,
	refresh,
	disabled,
	counterparty,
}: AssignmentPageItemNameMismatchProps) {
	const wireReceivedPaymentDetailsContentRef = React.useRef<HTMLDivElement>(null)
	const knownNameMatchScoresMapContentRef = React.useRef<HTMLDivElement>(null)
	useResetScrollTop(wireReceivedPaymentDetailsContentRef, [id])
	useResetScrollTop(knownNameMatchScoresMapContentRef, [id])

	const customerId = wireReceivedPayment?.relationships?.customer?.data.id?.toString()
	const creditorName = getWireReceivedPaymentCreditorName(wireReceivedPayment, receivingEntityName)

	const [showApprove, hideApprove] = useModal(() => {
		const successText = "Successfully approved Name Mismatch"
		const [state, callback] = useAsyncResultIdle(update)
		const [, addAliasCallback] = useAsyncResultIdle(addCustomerKnownNames)

		const onSubmit = ({reasonText}: {reasonText?: string} = {}) => {
			const data: NameMismatchApproveRequest = {
				type: AssignmentActionRequestType.nameMismatchApproveRequest,
				attributes: {reasonText},
			}
			return callback({accessToken, id, data})
		}

		const onAddAlias = () => {
			if (customerId && creditorName) {
				addAliasCallback(accessToken, customerId, creditorName)
			}
		}

		return (
			<AssignmentApproveRejectModal
				title="Approve Item"
				description={
					<>
						<span>You are about to approve Name Mismatch #{id}.</span>
						<br />
						<span>
							You can also choose to add <strong>{creditorName}</strong> as an alias for this customer.
						</span>
					</>
				}
				className="assignment-approve-reject-modal"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				reasonTextPlaceholder="Enter reason for your decision"
				primaryButtonText="Approve"
				primaryButtonClassname="is-success"
				isOptionalReasonText
				closeModal={hideApprove}
				onSuccess={refresh}
				successText={successText}
				cancelButtonText="Cancel"
				secondaryButtonText="Approve & Add Alias"
				secondaryButtonClassname="is-success is-outlined"
				onSecondaryButtonClick={(args: {reasonText: string}) => {
					onAddAlias()
					return onSubmit(args)
				}}
			/>
		)
	}, [id, accessToken, customerId, creditorName])

	const [showReject, hideReject] = useModal(() => {
		const successText = "Successfully rejected Name Mismatch"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reason, reasonText}: {reason: IncomingWireRejectReason; reasonText: string}) => {
			const data: NameMismatchRejectRequest = {
				type: AssignmentActionRequestType.nameMismatchRejectRequest,
				attributes: {reason, reasonText},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Reject Item"
				description={
					<>
						<span>You are about to reject Name Mismatch #{id}.</span>
						<br />
						<span>
							<strong>This action will reject Wire payment #{wireReceivedPayment?.id}.</strong>
						</span>
					</>
				}
				state={state}
				onSubmit={onSubmit}
				reasonsToEnableReasonText={Object.keys(incomingWireRejectReasons)}
				reasons={incomingWireRejectReasons}
				withReasonText
				reasonTextPlaceholder="Why are you rejecting this item?"
				primaryButtonText="Reject"
				primaryButtonClassname="is-danger"
				closeModal={hideReject}
				onSuccess={refresh}
				successText={successText}
				successClassname="has-text-black"
				cancelButtonText="Cancel"
			/>
		)
	}, [id, accessToken, wireReceivedPayment?.id])

	return (
		<AssignmentPageItem
			assignees={assignees}
			status={status}
			stage={stage}
			title={title}
			description={description}
			createdAt={createdAt}
			statusUpdatedAt={statusUpdatedAt}
			isOwnersItem={isOwnersItem}
			disabled={disabled}
			pageItemFooter={
				!disabled ? (
					<AssignmentPageItemApproveRejectFooter
						approveLabel="Approve"
						rejectLabel="Reject"
						onApprove={showApprove}
						onReject={showReject}
					/>
				) : undefined
			}
			pageCompletedItemHeader={
				<AssignmentPageCompletedItemHeader
					assignees={assignees}
					status={status}
					reason={reason ? incomingWireRejectReasons[reason].displayName : undefined}
					reasonText={reasonText}
				/>
			}
		>
			<GridContainer rows={1} columns={2} gap="8px" className="has-full-height assignment-page-item-default-inner-grid">
				<DetailsList
					title="Received Wire Details"
					icon="payment-box-incoming"
					className="scrollable-container"
					contentRef={wireReceivedPaymentDetailsContentRef}
					disabled={disabled}
				>
					{account && wireReceivedPayment && (
						<WireReceivedPaymentDetails
							wireReceivedPayment={wireReceivedPayment}
							receivingEntityName={receivingEntityName}
							sendingEntityName={sendingEntityName}
							disabled={disabled}
							singleColumn
							counterparty={counterparty}
						/>
					)}
				</DetailsList>
				<DetailsList
					title="Names at Unit"
					icon="document-folder-list"
					className="scrollable-container"
					contentRef={knownNameMatchScoresMapContentRef}
					disabled={disabled}
				>
					{knownNameMatchScoresMap &&
						(customerId ? (
							<NameMismatchNamesScoreTable
								accessToken={accessToken}
								customerId={customerId}
								namesScoreMap={knownNameMatchScoresMap}
								disabled={disabled}
							/>
						) : (
							<NamesScoreTable namesScoreMap={knownNameMatchScoresMap} disabled={disabled} />
						))}
				</DetailsList>
			</GridContainer>
		</AssignmentPageItem>
	)
}
